import { c as _c } from "react/compiler-runtime";
import { IDENTITY_ADDRESS_ANCHOR_DI, identityDisForShortTextFormat } from '@onefootprint/core';
import { Form, type LabelTooltipProps, Select, TextInput } from '@onefootprint/ui';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSubject } from '../../hooks/use-subject';
import type { CustomQuestionsFormData } from '../../types';
type DataIdentifierFieldProps = {
  questionIndex: number;
  label: string;
  placeholder?: string;
  tooltip?: LabelTooltipProps;
};
const stripPrefix = (di: string) => di.replace(/^(?:custom|id)\./, '');

/**
 * Edits a question's `dataIdentifier`, which stores the full DI: `custom.<field>`, or one of the
 * identity DIs this question's shape is allowed to write. The text box shows only the field name;
 * the `custom.`/`id.` prefix lives in the addon, so the stored value is always a complete DI.
 */
const DataIdentifierField = t0 => {
  const $ = _c(25);
  const {
    questionIndex,
    label,
    placeholder,
    tooltip
  } = t0;
  const {
    t
  } = useTranslation("playbook-custom-questions");
  const {
    control
  } = useFormContext();
  const subject = useSubject();
  const t1 = `questions.${questionIndex}.format` as never;
  let t2;
  if ($[0] !== control || $[1] !== t1) {
    t2 = {
      control,
      name: t1
    };
    $[0] = control;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const format = useWatch(t2) as string | undefined;
  const t3 = `questions.${questionIndex}.kind` as never;
  let t4;
  if ($[3] !== control || $[4] !== t3) {
    t4 = {
      control,
      name: t3
    };
    $[3] = control;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const kind = useWatch(t4) as string | undefined;
  let t5;
  if ($[6] !== format || $[7] !== kind) {
    t5 = kind === "address" ? [IDENTITY_ADDRESS_ANCHOR_DI] : identityDisForShortTextFormat(format);
    $[6] = format;
    $[7] = kind;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const identityDis = t5;
  const canBeIdentity = subject === "person" && identityDis.length > 0;
  const t6 = `questions.${questionIndex}.dataIdentifier`;
  let t7;
  if ($[9] !== identityDis || $[10] !== t) {
    t7 = {
      validate: value => {
        const di = typeof value === "string" ? value : "";
        if (di.startsWith("id.")) {
          return identityDis.includes(di) || t(_temp);
        }
        const suffix = di.replace(/^custom\./, "");
        if (!suffix) {
          return t(_temp2);
        }
        return /^[A-Za-z0-9_-]+$/.test(suffix) || t(_temp3);
      }
    };
    $[9] = identityDis;
    $[10] = t;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== canBeIdentity || $[13] !== identityDis || $[14] !== kind || $[15] !== label || $[16] !== placeholder || $[17] !== t || $[18] !== tooltip) {
    t8 = t9 => {
      const {
        field,
        fieldState
      } = t9;
      const value_0 = typeof field.value === "string" ? field.value : "";
      const isIdentity = value_0.startsWith("id.");
      const suffix_0 = stripPrefix(value_0);
      const identityDi = identityDis.includes(value_0) ? value_0 : identityDis[0];
      const displayedSuffix = isIdentity && kind === "address" ? t(_temp4) : suffix_0;
      const addon = canBeIdentity ? <Select size="compact" aria-label={t(_temp5)} value={isIdentity ? "identity" : "custom"} options={[{
        label: "id.",
        value: "identity"
      }, {
        label: "custom.",
        value: "custom"
      }]} className="[&_button]:w-auto" disabled={field.disabled} onChange={mode => field.onChange(mode === "identity" ? identityDi : `custom.${suffix_0}`)} /> : "custom.";
      return <Form.Group label={label} tooltip={tooltip} error={fieldState.error?.message} size="compact" addon={addon} disabled={field.disabled} fullWidth={true}>{isIdentity && identityDis.length > 1 ? <div className="flex-1"><Select aria-label={label} value={value_0} options={identityDis.map(_temp6)} disabled={field.disabled} hasError={!!fieldState.error} onChange={next => field.onChange(next)} onBlur={field.onBlur} size="compact" className="rounded-tl-none! rounded-bl-none!" /></div> : <TextInput ref={field.ref} value={displayedSuffix} placeholder={placeholder} disabled={field.disabled || isIdentity && canBeIdentity} hasError={!!fieldState.error} onChangeText={next_0 => field.onChange(`custom.${next_0}`)} onBlur={field.onBlur} size="compact" containerClassName="flex-1" className="rounded-tl-none! rounded-bl-none!" />}</Form.Group>;
    };
    $[12] = canBeIdentity;
    $[13] = identityDis;
    $[14] = kind;
    $[15] = label;
    $[16] = placeholder;
    $[17] = t;
    $[18] = tooltip;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== control || $[21] !== t6 || $[22] !== t7 || $[23] !== t8) {
    t9 = <Controller control={control} name={t6} rules={t7} render={t8} />;
    $[20] = control;
    $[21] = t6;
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  return t9;
};
export default DataIdentifierField;
function _temp($) {
  return $.form.dataIdentifierIdentityUnsupported;
}
function _temp2($_0) {
  return $_0.form.dataIdentifierRequired;
}
function _temp3($_1) {
  return $_1.form.dataIdentifierPattern;
}
function _temp4($_2) {
  return $_2.form.addressGroup;
}
function _temp5($_3) {
  return $_3.form.dataIdentifierPrefix;
}
function _temp6(di_0) {
  return {
    label: stripPrefix(di_0),
    value: di_0
  };
}